import type {
  TelephonyCallAction,
  TelephonyCallRow,
  TelephonySubjectDescriptor,
} from './types';

/**
 * Реестр действий над звонком.
 *
 * Upsert по `id` — безопасно для dev/HMR. Порядок = порядок регистрации.
 * Пустой реестр это норма: журнал в инстансе без потребителей работает
 * и без единого действия.
 */
class TelephonyCallActionRegistry {
  private static instance: TelephonyCallActionRegistry | null = null;

  private readonly actionsById = new Map<string, TelephonyCallAction>();

  static getInstance(): TelephonyCallActionRegistry {
    if (!TelephonyCallActionRegistry.instance) {
      TelephonyCallActionRegistry.instance = new TelephonyCallActionRegistry();
    }
    return TelephonyCallActionRegistry.instance;
  }

  register(action: TelephonyCallAction): void {
    this.actionsById.set(action.id, action);
  }

  getAvailable(call: TelephonyCallRow): TelephonyCallAction[] {
    return Array.from(this.actionsById.values()).filter(action => action.isAvailable(call));
  }

  getAll(): TelephonyCallAction[] {
    return Array.from(this.actionsById.values());
  }
}

/**
 * Реестр дескрипторов сущностей.
 *
 * Ключ — `entity_type` (`repository_code`), как в связи звонка.
 */
class TelephonySubjectRegistry {
  private static instance: TelephonySubjectRegistry | null = null;

  private readonly descriptorsByType = new Map<string, TelephonySubjectDescriptor>();

  static getInstance(): TelephonySubjectRegistry {
    if (!TelephonySubjectRegistry.instance) {
      TelephonySubjectRegistry.instance = new TelephonySubjectRegistry();
    }
    return TelephonySubjectRegistry.instance;
  }

  register(descriptor: TelephonySubjectDescriptor): void {
    this.descriptorsByType.set(descriptor.entityType, descriptor);
  }

  get(entityType: string | null | undefined): TelephonySubjectDescriptor | null {
    const normalized = entityType?.trim();
    if (!normalized) return null;

    return this.descriptorsByType.get(normalized) ?? null;
  }
}

const callActionRegistry = TelephonyCallActionRegistry.getInstance();
const subjectRegistry = TelephonySubjectRegistry.getInstance();

/**
 * Зарегистрировать действие над звонком (upsert по `id`).
 * Вызывается из `client/bootstrap.ts` модуля-владельца действия.
 */
export function registerTelephonyCallAction(action: TelephonyCallAction): TelephonyCallAction {
  callActionRegistry.register(action);
  return action;
}

/** Действия, доступные этому звонку; пустой список — рабочее состояние. */
export function getTelephonyCallActions(call: TelephonyCallRow): TelephonyCallAction[] {
  return callActionRegistry.getAvailable(call);
}

/**
 * Все зарегистрированные действия, без фильтра по звонку.
 *
 * Нужны тулбару: его кнопки стоят на месте и когда строка не выбрана —
 * набор кнопок, скачущий от строки к строке, читать невозможно.
 */
export function getAllTelephonyCallActions(): TelephonyCallAction[] {
  return callActionRegistry.getAll();
}

/**
 * Зарегистрировать дескриптор сущности (upsert по `entityType`).
 * Вызывается из `client/bootstrap.ts` модуля-владельца сущности.
 */
export function registerTelephonySubject(
  descriptor: TelephonySubjectDescriptor
): TelephonySubjectDescriptor {
  subjectRegistry.register(descriptor);
  return descriptor;
}

/** Дескриптор по `entity_type` или null — тогда название показывается без ссылки. */
export function getTelephonySubjectDescriptor(
  entityType: string | null | undefined
): TelephonySubjectDescriptor | null {
  return subjectRegistry.get(entityType);
}
